import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import PublicIcon from "@mui/icons-material/Public";
import { usePage } from "@hooks/usePage";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { useEffect } from "react";
import { globalMutate } from "@utils/swr";
import { useGetCameraTrapById } from "@services/device/cameraTrap/getById";
import { updateCameraTrap } from "@services/device/cameraTrap/update";
import { validateCoordinates } from "@utils/formatters";
import Heading from "@commons/Typography/Heading";
import Map from "@commons/Map";
import Text from "@commons/Typography/Text";
import {
  cameraTrapEditSchema,
  cameraTrapEditDefaultValues,
  type CameraTrapEditFormData,
} from "../../schemas/cameraTrapEditSchema";

function ModalEditCameraTrap() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal, dataModal } =
    usePage();
  const open = isModalOpen("editCameraTrap");

  const id = dataModal?.id as string;
  const { data: cameraTrap, isLoading, error } = useGetCameraTrapById(id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<CameraTrapEditFormData>({
    resolver: zodResolver(cameraTrapEditSchema),
    defaultValues: cameraTrapEditDefaultValues,
  });

  useEffect(() => {
    if (open && cameraTrap) {
      setValue("number", String(cameraTrap.number));
      setValue("responsibleTeam", cameraTrap.responsibleTeam || "");
      setValue("latitude", String(cameraTrap.latitude));
      setValue("longitude", String(cameraTrap.longitude));
    } else if (!open) {
      reset();
    }
  }, [open, cameraTrap, setValue, reset]);

  const onSubmit = async (data: CameraTrapEditFormData) => {
    try {
      setIsLoading(true);

      await updateCameraTrap(id, {
        number: data.number,
        responsibleTeam: data.responsibleTeam || undefined,
        latitude: Number(data.latitude),
        longitude: Number(data.longitude),
      });

      toast.success("Câmera atualizada com sucesso");

      await globalMutate([
        "fetch-get-camera-traps",
        "fetch-camera-traps-geojson",
        `fetch-camera-trap-${id}`,
      ]);

      handleClose();
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : "Erro ao atualizar Câmera Trap";
      toast.error(title);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Editar Câmera Trap"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-4xl"
      isLoading={isLoading}
      error={!!error}
    >
      <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 gap-x-6">
        <div className="flex flex-col gap-4">
          <Controller
            name="number"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Número do dispositivo"
                textSize="sm"
                fullWidth
                error={!!errors.number}
                helperText={errors.number?.message}
              />
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <Controller
              name="latitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Latitude"
                  type="number"
                  textSize="sm"
                  fullWidth
                  error={!!errors.latitude}
                  helperText={errors.latitude?.message}
                />
              )}
            />
            <Controller
              name="longitude"
              control={control}
              render={({ field }) => (
                <TextInput
                  {...field}
                  label="Longitude"
                  type="number"
                  fullWidth
                  error={!!errors.longitude}
                  helperText={errors.longitude?.message}
                />
              )}
            />
          </div>

          <Controller
            name="responsibleTeam"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Equipe Responsável"
                type="text"
                textSize="sm"
                fullWidth
                error={!!errors.responsibleTeam}
                helperText={errors.responsibleTeam?.message}
              />
            )}
          />
        </div>

        <div className="h-64 lg:h-full w-full order-first lg:order-none">
          {hasValidCoordinates ? (
            <Map.Root>
              <Map.Localization latitude={lat} longitude={lng} draw />
            </Map.Root>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
              <PublicIcon className="text-4xl text-gray-400" />
              <Heading size="sm" className="text-gray-600">
                Mapa
              </Heading>
              <Text size="sm">
                Insira coordenadas válidas para visualizar o mapa.
              </Text>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalEditCameraTrap;
